import React from 'react';
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import { ConsensusData, ConsensusVote } from '../services/api';

interface ConsensusPanelProps {
  data: ConsensusData;
}

const featureIcon: Record<string, string> = {
  'Stock Scoring': '📊',
  'Trading Signals': '📈',
  'Recommendations': '💡',
};

function VoteRow({ vote }: { vote: ConsensusVote }) {
  // Chip and LinearProgress do NOT share a colour vocabulary. Chip accepts 'default';
  // LinearProgress does not — it resolves the name through theme.palette[name].main, and
  // theme.palette.default is undefined, so a NEUTRAL vote threw
  // "Cannot read properties of undefined (reading 'main')" and unmounted the whole page.
  // That is what turned Stock Scoring into a blank white screen for any symbol with even
  // one neutral vote, which is most of them. The `as any` casts that used to sit on both
  // props are exactly what let the type checker miss it; they are gone.
  const chipColor: 'success' | 'error' | 'default' =
    vote.direction === 'BULLISH' ? 'success'
    : vote.direction === 'BEARISH' ? 'error'
    : 'default';
  const barColor: 'success' | 'error' | 'inherit' =
    vote.direction === 'BULLISH' ? 'success'
    : vote.direction === 'BEARISH' ? 'error'
    : 'inherit';

  const icon = vote.direction === 'BULLISH'
    ? <TrendingUpIcon fontSize="small" />
    : vote.direction === 'BEARISH'
      ? <TrendingDownIcon fontSize="small" />
      : <RemoveIcon fontSize="small" />;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
      <Typography sx={{ width: 20, fontSize: '1rem', flexShrink: 0 }}>
        {featureIcon[vote.feature] ?? '•'}
      </Typography>
      <Typography variant="body2" sx={{ width: 140, flexShrink: 0, fontWeight: 500 }}>
        {vote.feature}
      </Typography>
      <Chip
        icon={icon}
        label={vote.signal.replace('_', ' ')}
        color={chipColor}
        size="small"
        sx={{ minWidth: 100, fontWeight: 600, fontSize: '0.7rem' }}
      />
      <Box sx={{ flex: 1, minWidth: 60 }}>
        <Tooltip title={`${Math.round(vote.confidence * 100)}% confidence`}>
          <LinearProgress
            variant="determinate"
            value={vote.confidence * 100}
            color={barColor}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Tooltip>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={vote.short_reason}
      >
        {vote.short_reason}
      </Typography>
    </Box>
  );
}

function AgreementBar({ bullish, bearish, neutral, total }: {
  bullish: number; bearish: number; neutral: number; total: number;
}) {
  const bPct = (bullish / total) * 100;
  const sPct = (bearish / total) * 100;
  const nPct = (neutral / total) * 100;
  return (
    <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', mt: 0.5 }}>
      {bPct > 0 && <Box sx={{ width: `${bPct}%`, bgcolor: 'success.main' }} />}
      {nPct > 0 && <Box sx={{ width: `${nPct}%`, bgcolor: 'grey.400' }} />}
      {sPct > 0 && <Box sx={{ width: `${sPct}%`, bgcolor: 'error.main' }} />}
    </Box>
  );
}

const ConsensusPanel: React.FC<ConsensusPanelProps> = ({ data }) => {
  const total = data.votes.length;
  const overallColor =
    data.agreement === 'STRONG_BULL' || data.agreement === 'BULL'
      ? 'success.main'
      : data.agreement === 'STRONG_BEAR' || data.agreement === 'BEAR'
        ? 'error.main'
        : 'warning.main';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Cross-Feature Consensus
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {data.symbol}
        </Typography>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {data.votes.map((vote) => (
        <VoteRow key={vote.feature} vote={vote} />
      ))}

      <Divider sx={{ my: 1.5 }} />

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: overallColor }}>
            {data.agreement_label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data.bullish_count}B / {data.neutral_count}H / {data.bearish_count}S
          </Typography>
        </Box>
        <AgreementBar
          bullish={data.bullish_count}
          neutral={data.neutral_count}
          bearish={data.bearish_count}
          total={total}
        />
      </Box>
    </Box>
  );
};

export default ConsensusPanel;
